/* Galaxy Lighting - sticky header, v6 (2026-07-28)

   WHY THIS LOOKS THE WAY IT DOES

   v1 (Dawn stock) had three faults:
     - Hid the header on a 1px scroll delta, so a finger wobble snapped it away.
     - Its preventReveal flag, set by any mega menu, sent the scroll-UP branch
       into hide(). After opening a menu once, scrolling up hid the header too.
     - hide/reveal toggled classes with no transition.

   v2 accumulated px per direction. Fine in a synthetic test where one scroll
   event jumps 800px, wrong on a real trackpad: momentum tails emit a
   non-monotonic stream that wobbles a few px BACKWARDS near the end, which
   accumulated past the reveal threshold and popped the header back mid-gesture.

   v3 added a direction-hold timer and broke outright, for two reasons that only
   surfaced once the element's internal state was actually inspected:

     1. this.dir COLLIDES WITH HTMLElement.dir - the native text-direction
        attribute present on every HTML element, which coerces to a string.
        Assigning 1 / -1 read back as "", so (d !== this.dir) was ALWAYS true:
        every scroll event restarted the gesture and travel never accumulated.
        The header simply never hid.
        => every internal field is now prefixed gx to stay clear of the DOM.

     2. .section-header reports offsetHeight === 1. It is a position:sticky
        wrapper whose real content lives in a child, so the TOP_ZONE guard that
        depended on it was meaningless.
        => height is measured from the inner .header element instead.

   v4 still revealed on a +-4px jitter, because 4 > NOISE and a few of them
   summed past the reveal threshold.

   v5 added the PEAK test. Measured on this site, a real upward flick starts with
   a ~25px delta and decays (25,20,16,13,10,8,7,5,4,3,3,2,2,1,1). Hand jitter is
   a flat +-4px oscillation with no large delta anywhere. So a gesture must
   produce at least one delta of PEAK_PX before it may change state - distance
   alone is not enough.

   v6 removes the .animate class. Dawn's base.css ships
     .section-header.animate { transition: top .15s ease-out; }
   and we were adding that class ourselves. Dawn moves the header with `top`;
   this theme moves it with `transform`. So .animate replaced our transform
   transition with one for a property we never touch, and the reveal had no
   visible animation at all - it just snapped. Nothing here needs .animate.

   THE MODEL:
     - Deltas below NOISE px are discarded outright.
     - Travel and peak accrue per gesture; a direction flip starts a new gesture.
     - A flip is only believed once it has covered FLIP_PX, so a momentum bounce
       inside a flick is a non-event.
     - State only changes when BOTH the distance AND the peak thresholds are met.
     - Inside BOTTOM_ZONE of the document end, state is frozen - that is the
       rubber-band region, and reacting to it made overshooting at the bottom
       collapse the header.
     - Scrolling up always reveals. No preventReveal, no exceptions.
     - The header never moves while a menu / search / drawer is open.
*/
(function () {
  if (window.__gxSticky) return;
  window.__gxSticky = true;

  var NOISE = 2;        // ignore deltas smaller than this
  var PEAK_PX = 10;     // a gesture needs at least one delta this big to count
  var HIDE_PX = 70;     // sustained downward travel before hiding
  var SHOW_PX = 24;     // upward travel before revealing
  var FLIP_PX = 8;      // a reversal must cover this much before it is believed
  var TOP_ZONE = 120;   // never hide this close to the top of the page
  var BOTTOM_ZONE = 90; // never change state inside the rubber-band zone

  class GalaxyStickyHeader extends HTMLElement {
    connectedCallback() {
      this.gxHeader = document.querySelector('.section-header');
      if (!this.gxHeader) return;

      var type = this.getAttribute('data-sticky-type');
      this.gxAlways = type === 'always' || type === 'reduce-logo-size';

      this.gxLastY = this.gxScrollY();
      this.gxDir = 0;      // -1 up, 1 down, 0 unknown
      this.gxTravel = 0;   // px travelled in the current gesture
      this.gxPeak = 0;     // largest single delta in the current gesture
      this.gxPending = 0;  // px travelled against gxDir, awaiting FLIP_PX
      this.gxHidden = false;
      this.gxTicking = false;
      this.gxHeight = 0;

      this.gxMeasure();
      if (this.gxAlways) this.gxHeader.classList.add('shopify-section-header-sticky');

      this.gxOnScroll = this.gxScroll.bind(this);
      this.gxOnResize = this.gxMeasure.bind(this);

      window.addEventListener('scroll', this.gxOnScroll, { passive: true });
      window.addEventListener('resize', this.gxOnResize, { passive: true });
      window.matchMedia('(max-width: 990px)').addEventListener('change', this.gxOnResize);

      /* Dawn's mega menus dispatch this to stop the header reappearing.
         Deliberately ignored - scrolling up must always reveal. */
      this.addEventListener('preventHeaderReveal', function () {});
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.gxOnScroll);
      window.removeEventListener('resize', this.gxOnResize);
    }

    gxScrollY() {
      return window.pageYOffset || document.documentElement.scrollTop || 0;
    }

    /* .section-header is a 1px-tall sticky wrapper; the real bar is the inner
       .header element. Fall back to the wrapper, then to a sane default. */
    gxMeasure() {
      if (!this.gxHeader) return;
      var inner = this.gxHeader.querySelector('.header');
      var h = (inner && inner.offsetHeight) || this.gxHeader.offsetHeight || 0;
      if (h < 20) h = 120;
      this.gxHeight = h;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }

    gxMenuOpen() {
      if (!this.gxHeader) return false;
      if (this.gxHeader.querySelector('details[open]')) return true;
      if (document.querySelector('.menu-drawer-container details[open]')) return true;
      if (document.body.classList.contains('overflow-hidden')) return true;
      var ps = this.querySelector && this.querySelector('predictive-search');
      if (ps && ps.isOpen) return true;
      return false;
    }

    gxAtBottom(y) {
      var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return (max - y) <= BOTTOM_ZONE;
    }

    gxScroll() {
      if (this.gxTicking) return;
      this.gxTicking = true;
      requestAnimationFrame(this.gxUpdate.bind(this));
    }

    gxNewGesture(d, mag) {
      this.gxDir = d;
      this.gxTravel = mag;
      this.gxPeak = mag;
      this.gxPending = 0;
    }

    gxUpdate() {
      this.gxTicking = false;
      if (!this.gxHeader) return;

      var y = this.gxScrollY();
      var dy = y - this.gxLastY;
      this.gxLastY = y;

      if (this.gxMenuOpen()) return;

      /* Back at the very top: reset to the in-flow header. */
      if (y <= 0) {
        this.gxDir = 0; this.gxTravel = 0; this.gxPeak = 0; this.gxPending = 0;
        this.gxReset();
        return;
      }

      /* Rubber-band / overscroll at the document end: hold current state. */
      if (this.gxAtBottom(y)) return;

      /* Momentum jitter. */
      if (Math.abs(dy) < NOISE) return;

      var d = dy > 0 ? 1 : -1;
      var mag = Math.abs(dy);

      if (this.gxDir === 0) {
        this.gxNewGesture(d, mag);
      } else if (d === this.gxDir) {
        this.gxTravel += mag;
        if (mag > this.gxPeak) this.gxPeak = mag;
        this.gxPending = 0;   // continuing forward cancels a pending reversal
      } else {
        /* Moving against the gesture. Only believe it once it has covered
           FLIP_PX - this is what makes a momentum bounce a non-event. */
        this.gxPending += mag;
        if (this.gxPending < FLIP_PX) return;
        this.gxNewGesture(d, this.gxPending);
      }

      /* Intent test: distance alone is not enough. Hand jitter can drift a long
         way in small steps; a real gesture always contains one big delta. */
      if (this.gxPeak < PEAK_PX) return;

      this.gxHeader.classList.add('scrolled-past-header');

      if (this.gxDir < 0) {
        if (this.gxTravel >= SHOW_PX) this.gxReveal();
      } else {
        if (this.gxTravel >= HIDE_PX && y > TOP_ZONE + this.gxHeight) this.gxHide();
      }
    }

    /* NB: we deliberately do NOT add Dawn's .animate class - see the v6 note at
       the top. It carries `transition: top .15s`, which would override the
       transform transition this theme actually uses. */
    gxHide() {
      if (this.gxAlways || this.gxHidden) return;
      this.gxHidden = true;
      this.gxHeader.classList.add('shopify-section-header-sticky', 'shopify-section-header-hidden');
      this.gxCloseMenus();
    }

    gxReveal() {
      if (this.gxAlways) return;
      this.gxHeader.classList.add('shopify-section-header-sticky');
      if (!this.gxHidden) return;
      this.gxHidden = false;
      this.gxHeader.classList.remove('shopify-section-header-hidden');
    }

    gxReset() {
      if (this.gxAlways) return;
      this.gxHidden = false;
      this.gxHeader.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate', 'scrolled-past-header');
    }

    gxCloseMenus() {
      var d = this.gxHeader.querySelectorAll('header-menu');
      for (var i = 0; i < d.length; i++) { try { d[i].close(); } catch (e) {} }
      var m = this.gxHeader.querySelector('details-modal');
      if (m) { try { m.close(false); } catch (e) {} }
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', GalaxyStickyHeader);
  }

  /* Dawn's compiled bundle also defines 'sticky-header'. We got there first, so
     its call throws NotSupportedError - harmless, but it shows as a red console
     error. Swallow that ONE name; every other define() passes through so real
     duplicate-registration bugs still surface. */
  var nativeDefine = customElements.define.bind(customElements);
  customElements.define = function (name, ctor, options) {
    if (name === 'sticky-header' && customElements.get(name)) return;
    return nativeDefine(name, ctor, options);
  };
})();
